import type { Schemas } from '@npc/api-client'
import { useState, type ReactNode } from 'react'

import { CapabilityRow } from './capability'
import { Button } from '../../components/button'
import { Card, CardTitle } from '../../components/card'
import { cn } from '../../components/cn'
import { Modal } from '../../components/modal'
import { Page, PageHeader } from '../../components/page'
import { ScreenError, ScreenLoading } from '../../components/state'
import {
  useDeleteVendorProjectLink,
  useDeleteVendorPublicProjectLink,
  useProjects,
  useUpsertVendorProjectLink,
  useVendors,
} from '../../app/queries'
import { t } from '../../copy'
import { T } from '../../copy/text'
import './vendors.css'

/**
 * 屏 `vendor` 服务商与账号。
 *
 * 这一屏是 PRD「第 1 号痛点」的另一半（记不清账号密码在哪儿）。
 * 做了的是**列表这一半**：六张卡、每家的能力四态、到期与欠费提醒、空状态。
 *
 * **没做的是「点任意一家改账号密码和密钥」那个弹窗**，理由见文件末尾。
 *
 * ## 一家服务商是一堆能力，不是一个 kind
 *
 * `Vendor.kind` 保留着，但契约把它的含义收窄成「主要是干什么的」，
 * **能力判断一律看 `capabilities`**——第一个真实档案里 Cloudflare 一家占了 5 种，
 * 而且每种的状态都不一样（DNS 在用、R2 有凭证但没开通、CDN 只对一部分用）。
 *
 * 顺带：`vendor.kind.*` 那几条人话说法 `docs/copy` 里也没有，
 * 所以卡上不显示 kind——显示 `cloud` / `hosting` 这种内部值是不行的。
 * 「这家是干嘛的」由 `usage_note` 那一行回答，那是后端给的人话。
 */
export function VendorScreen() {
  const vendors = useVendors()
  const projects = useProjects()
  const [editingVendorId, setEditingVendorId] = useState<string | null>(null)

  if (vendors.isPending || projects.isPending) return <ScreenLoading shape="cards" cols={3} />
  if (vendors.error) return <ScreenError error={vendors.error} />
  if (projects.error) return <ScreenError error={projects.error} />

  const items = vendors.data?.items ?? []
  const projectChoices = (projects.data?.items ?? []).filter((project) => !project.is_builtin)
  const editingVendor = editingVendorId
    ? items.find((vendor) => vendor.id === editingVendorId)
    : undefined

  return (
    <Page className="vendors-theme">
      <PageHeader
        title={<T id="vendor.page.title" />}
        subtitle={<T id="vendor.page.subtitle" />}
        className="mb-npc-modal"
      />

      {items.length === 0 ? (
        <p className="m-0 text-npc-body-sm text-npc-ink-soft">
          <T id="vendor.empty" />
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-npc-grid">
          {items.map((vendor) => (
            <VendorCard key={vendor.id} vendor={vendor} onEditScope={() => setEditingVendorId(vendor.id)} />
          ))}
        </div>
      )}

      {editingVendor ? (
        <VendorScopeModal
          key={editingVendor.id}
          open
          vendor={editingVendor}
          projects={projectChoices}
          onOpenChange={(open) => {
            if (!open) setEditingVendorId(null)
          }}
        />
      ) : null}
    </Page>
  )
}

function VendorCard({
  vendor,
  onEditScope,
}: {
  vendor: Schemas['Vendor']
  onEditScope: () => void
}) {
  return (
    <Card className="flex flex-col gap-npc-gap-sm">
      <div className="flex items-start justify-between gap-npc-gap-sm">
        <CardTitle>{vendor.name}</CardTitle>
        <Button size="sm" variant="ghost" onClick={onEditScope}>
          <T id="vendor.action.edit_scope" />
        </Button>
      </div>

      {/* 「这家是干嘛的 / 在用多少」。后端给的人话，不是拿 kind 拼的 */}
      {vendor.usage_note ? (
        <div>
          <span className="text-npc-meta text-npc-ink-softer">
            <T id="vendor.card.usage" />
          </span>
          <div className="text-npc-caption leading-[1.5] text-npc-ink-medium">
            {vendor.usage_note}
          </div>
        </div>
      ) : null}

      <ProjectLinks links={vendor.project_links ?? []} />

      {vendor.monthly_cost ? (
        <div>
          <span className="text-npc-meta text-npc-ink-softer">
            <T id="vendor.card.monthly_cost" />
          </span>
          {/* 钱是十进制字符串不是 number。币种与金额分开给，
              这里只把它们摆在一起，不做汇率也不改格式 */}
          <div className="font-npc-display text-npc-body-lg font-medium">
            {vendor.monthly_cost}
            <span className="ml-npc-gap-xs text-npc-caption text-npc-ink-soft">
              {vendor.currency}
            </span>
          </div>
        </div>
      ) : null}

      {vendor.capabilities && vendor.capabilities.length > 0 ? (
        <div className="flex flex-col gap-npc-gap-xs border-t border-npc-border-soft pt-npc-gap-sm">
          {vendor.capabilities.map((capability) => (
            <CapabilityRow key={capability.id} capability={capability} />
          ))}
        </div>
      ) : null}

      <Due vendor={vendor} />
    </Card>
  )
}

function VendorScopeModal({
  open,
  vendor,
  projects,
  onOpenChange,
}: {
  open: boolean
  vendor: Schemas['Vendor']
  projects: Schemas['Project'][]
  onOpenChange: (open: boolean) => void
}) {
  const publicLink = (vendor.project_links ?? []).find((link) => link.scope === 'public')
  const projectLinks = (vendor.project_links ?? []).filter((link) => link.scope === 'project' && link.project)
  const [publicNote, setPublicNote] = useState(publicLink?.usage_note ?? '')
  const [projectId, setProjectId] = useState(projects[0]?.id ?? '')
  const [projectNote, setProjectNote] = useState('')
  const upsert = useUpsertVendorProjectLink(vendor.id)
  const deletePublic = useDeleteVendorPublicProjectLink(vendor.id)
  const deleteProject = useDeleteVendorProjectLink(vendor.id)

  const saving = upsert.isPending
  const removing = deletePublic.isPending || deleteProject.isPending
  const canSavePublic = publicNote.trim().length > 0 && !saving
  const canSaveProject = projectId.length > 0 && projectNote.trim().length > 0 && !saving

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={<T id="vendor.scope.editor.title" />}
      className="w-[min(680px,calc(100vw-48px))]"
    >
      <div className="grid gap-npc-card">
        <label className="grid gap-npc-gap-xs">
          <span className="text-npc-caption text-npc-ink-soft">
            <T id="vendor.scope.editor.public_note" />
          </span>
          <textarea
            value={publicNote}
            onChange={(event) => setPublicNote(event.target.value)}
            aria-label={t('vendor.scope.editor.public_note')}
            className={inputClass}
            rows={3}
          />
        </label>
        <div>
          <Button
            size="sm"
            variant="primary"
            disabled={!canSavePublic}
            onClick={() =>
              upsert.mutate({
                scope: 'public',
                usage_note: publicNote.trim(),
              })
            }
          >
            <T id="vendor.action.save_public_scope" />
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-npc-gap-sm lg:grid-cols-[minmax(0,180px)_1fr_auto] lg:items-end">
          <label className="grid gap-npc-gap-xs">
            <span className="text-npc-caption text-npc-ink-soft">
              <T id="vendor.scope.editor.project" />
            </span>
            <select
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
              aria-label={t('vendor.scope.editor.project')}
              className={selectClass}
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-npc-gap-xs">
            <span className="text-npc-caption text-npc-ink-soft">
              <T id="vendor.scope.editor.project_note" />
            </span>
            <textarea
              value={projectNote}
              onChange={(event) => setProjectNote(event.target.value)}
              aria-label={t('vendor.scope.editor.project_note')}
              className={inputClass}
              rows={2}
            />
          </label>
          <Button
            size="sm"
            variant="secondary"
            disabled={!canSaveProject}
            onClick={() =>
              upsert.mutate(
                {
                  scope: 'project',
                  project_id: projectId,
                  usage_note: projectNote.trim(),
                },
                { onSuccess: () => setProjectNote('') },
              )
            }
          >
            <T id="vendor.action.save_project_scope" />
          </Button>
        </div>

        {projectLinks.length > 0 || publicLink ? (
          <div>
            <div className="mb-npc-gap-xs text-npc-caption text-npc-ink-soft">
              <T id="vendor.scope.editor.current" />
            </div>
            <div className="grid gap-npc-gap-xs">
              {publicLink ? (
                <EditableLinkRow
                  title={<T id="vendor.scope.public" />}
                  note={publicLink.usage_note}
                  disabled={removing}
                  removeLabel={t('vendor.action.remove_public_scope')}
                  onRemove={() => deletePublic.mutate()}
                />
              ) : null}
              {projectLinks.map((link) => (
                <EditableLinkRow
                  key={link.project!.id}
                  title={link.project!.name}
                  note={link.usage_note}
                  disabled={removing}
                  removeLabel={t('vendor.action.remove_project_scope', { project: link.project!.name })}
                  onRemove={() => deleteProject.mutate(link.project!.id)}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  )
}

function EditableLinkRow({
  title,
  note,
  removeLabel,
  disabled,
  onRemove,
}: {
  title: ReactNode
  note: string
  removeLabel: string
  disabled: boolean
  onRemove: () => void
}) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-npc-gap-sm rounded-npc-inset border border-npc-border-soft px-npc-grid-sm py-npc-row-y">
      <div className="min-w-0">
        <div className="text-npc-meta font-medium text-npc-ink">{title}</div>
        <div className="mt-[2px] text-npc-caption leading-[1.5] text-npc-ink-medium">{note}</div>
      </div>
      <Button size="sm" variant="danger" disabled={disabled} aria-label={removeLabel} onClick={onRemove}>
        <T id="vendor.action.remove_scope" />
      </Button>
    </div>
  )
}

function ProjectLinks({ links }: { links: Schemas['VendorProjectLink'][] }) {
  const visible = links.filter((link) => link.scope === 'public' || link.project)
  if (visible.length === 0) return null

  return (
    <div>
      <span className="text-npc-meta text-npc-ink-softer">
        <T id="vendor.card.scope" />
      </span>
      <div className="mt-npc-gap-xs flex flex-col gap-npc-gap-xs">
        {visible.map((link, index) => (
          <div
            key={`${link.scope}-${link.project?.id ?? 'public'}-${index}`}
            className="rounded-npc-inset border border-npc-border-soft px-npc-grid-sm py-npc-row-y"
          >
            <div className="text-npc-meta font-medium text-npc-ink">
              {link.scope === 'public' ? <T id="vendor.scope.public" /> : link.project?.name}
            </div>
            <div className="mt-[2px] text-npc-caption leading-[1.5] text-npc-ink-medium">
              {link.usage_note}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * 到期与欠费。
 *
 * **`days_left` 由后端算好给，前端不拿 `due_at` 减当前时间**——契约里那句提醒
 * 很具体：两边时区一旦不一致，「还有 1 天」和「已经欠费」会同时出现在两个地方。
 *
 * `needs_reminder` 同理：开了自动续的一律为 false，「该不该提醒」是后端的判断。
 */
function Due({ vendor }: { vendor: Schemas['Vendor'] }) {
  if (vendor.auto_renew) {
    return (
      <p className="m-0 text-npc-caption text-npc-ink-soft">
        <T id="vendor.auto_renew_on" />
      </p>
    )
  }

  if (vendor.days_left === null || vendor.days_left === undefined) return null

  const overdue = vendor.days_left < 0

  return (
    <p className={cn('m-0 text-npc-caption', overdue ? 'text-npc-red-ink' : 'vd-due-soon')}>
      {overdue ? (
        <T id="vendor.overdue" vars={{ vendor: vendor.name }} />
      ) : (
        <T id="vendor.due_soon" vars={{ vendor: vendor.name, days: vendor.days_left }} />
      )}
    </p>
  )
}

const inputClass =
  'block w-full resize-y rounded-npc-card-sm border border-npc-border-soft bg-npc-surface px-npc-grid-sm py-npc-gap-sm ' +
  'text-npc-body-sm leading-[1.6] text-npc-ink outline-none focus:border-[color:var(--vd-accent-border)]'

const selectClass =
  'block h-[38px] w-full rounded-npc-card-sm border border-npc-border-soft bg-npc-panel px-npc-grid-sm ' +
  'text-npc-body-sm text-npc-ink outline-none focus:border-[color:var(--vd-accent-border)]'

/*
 * 「点任意一家可以改它的账号密码和密钥」那个弹窗**没做**，卡在两处：
 *
 * 一、**密钥那一半的字段不存在。** `docs/copy/credential.yaml` 里
 *     `credential.scope.too_wide`（「这把钥匙管得太宽了——它能改账单、能把域名转走」）、
 *     `credential.blast.*`、`credential.derived.also_dies`、
 *     `credential.source.leaked_channel`、`credential.rotation.pending`
 *     句子全都写好了，而 `CredentialMeta` 上只有 `label` / `live` / `kept_text`——
 *     `scope_summary` / `is_overprivileged` / `blast_radius` / `derived_from` /
 *     `source_channel` / `rotation_state` 一个都没有。
 *
 *     **这是「句子有了但没有载体」的第二例**（第一例是 `remedy.*`）。
 *     没有这些字段，弹窗里的密钥那半边只能显示「已保管」——
 *     而那正好是这一屏最该说清楚的地方：档案里那把 Token 有 273 个权限组、
 *     能改账单能转域名。
 *
 * 二、「看一眼」要走 `POST /credentials/{id}/reveal` + Passkey 二次验证，
 *     那是红级动作，与弹窗一起做才说得清。
 *
 * 逐条写在 `apps/console/docs/m3-需要的契约.md`。
 */
